import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import {
  fetchAdminUsers,
  fetchAdminUserById,
  enableUser,
  disableUser,
  addUserRole,
  removeUserRole,
  deleteUser,
  fetchUserVideoPurchases,
  fetchUserLast10PurchasesAfterInfluencerCodeUsage,
  exportUsersPurchasesToExcel,
  fetchUserFootagePurchaseHours,
  clearErrors,
  clearSelectedUser,
  setUsersPage,
  setUsersRowsPerPage,
  setVideoPurchasesPage,
  setVideoPurchasesRowsPerPage,
  setFootageHoursPage,
  setFootageHoursRowsPerPage
} from '../store/adminUsersSlice';

export const useAdminUsers = () => {
  const dispatch = useDispatch();
  
  // Select state from Redux store
  const {
    // Users data
    users,
    usersLoading,
    usersError,
    
    // Pagination
    usersPage,
    usersRowsPerPage,
    usersTotalElements,
    usersTotalPages,
    
    // Selected user details
    selectedUser,
    selectedUserLoading,
    selectedUserError,
    
    // User actions
    actionLoading,
    actionError,
    
    // Video purchases
    videoPurchases,
    videoPurchasesLoading,
    videoPurchasesError,
    videoPurchasesPage,
    videoPurchasesRowsPerPage,
    videoPurchasesTotalElements,
    videoPurchasesTotalPages,
    
    // Influencer purchases
    influencerPurchases,
    influencerPurchasesLoading,
    influencerPurchasesError,
    
    // Footage hours
    footageHours,
    footageHoursLoading,
    footageHoursError,
    footageHoursPage,
    footageHoursRowsPerPage,
    footageHoursTotalElements,
    footageHoursTotalPages,
    
    // Export
    exportLoading,
    exportError
  } = useSelector(state => state.adminUsers);

  // ───────────────────────── User Management Actions ─────────────────────────

  const loadUsers = useCallback((pageable = {}) => {
    dispatch(fetchAdminUsers(pageable));
  }, [dispatch]);

  const loadUserById = useCallback((userId) => {
    dispatch(fetchAdminUserById(userId));
  }, [dispatch]);

  const handleEnableUser = useCallback((userId) => {
    dispatch(enableUser(userId));
  }, [dispatch]);

  const handleDisableUser = useCallback((userId) => {
    dispatch(disableUser(userId));
  }, [dispatch]);

  const handleAddUserRole = useCallback((userId, roleName) => {
    dispatch(addUserRole({ userId, roleName }));
  }, [dispatch]);

  const handleRemoveUserRole = useCallback((userId, roleName) => {
    dispatch(removeUserRole({ userId, roleName }));
  }, [dispatch]);

  const handleDeleteUser = useCallback((userId) => {
    dispatch(deleteUser(userId));
  }, [dispatch]);

  // ───────────────────────── Purchase Management Actions ─────────────────────────

  const loadUserVideoPurchases = useCallback((pageable = {}) => {
    dispatch(fetchUserVideoPurchases(pageable));
  }, [dispatch]);

  const loadUserLast10PurchasesAfterInfluencerCodeUsage = useCallback((userId) => {
    dispatch(fetchUserLast10PurchasesAfterInfluencerCodeUsage(userId));
  }, [dispatch]);

  const handleExportUsersPurchasesToExcel = useCallback((months, year) => {
    dispatch(exportUsersPurchasesToExcel({ months, year }));
  }, [dispatch]);

  const loadUserFootagePurchaseHours = useCallback((pageable = {}) => {
    dispatch(fetchUserFootagePurchaseHours(pageable));
  }, [dispatch]);

  // ───────────────────────── Pagination Actions ─────────────────────────

  const handleUsersPageChange = useCallback((page) => {
    dispatch(setUsersPage(page));
  }, [dispatch]);

  const handleUsersRowsPerPageChange = useCallback((rowsPerPage) => {
    dispatch(setUsersRowsPerPage(rowsPerPage));
  }, [dispatch]);

  const handleVideoPurchasesPageChange = useCallback((page) => {
    dispatch(setVideoPurchasesPage(page));
  }, [dispatch]);

  const handleVideoPurchasesRowsPerPageChange = useCallback((rowsPerPage) => {
    dispatch(setVideoPurchasesRowsPerPage(rowsPerPage));
  }, [dispatch]);

  const handleFootageHoursPageChange = useCallback((page) => {
    dispatch(setFootageHoursPage(page));
  }, [dispatch]);

  const handleFootageHoursRowsPerPageChange = useCallback((rowsPerPage) => {
    dispatch(setFootageHoursRowsPerPage(rowsPerPage));
  }, [dispatch]);

  // ───────────────────────── Utility Actions ─────────────────────────

  const clearAllErrors = useCallback(() => {
    dispatch(clearErrors());
  }, [dispatch]);

  const clearUserDetails = useCallback(() => {
    dispatch(clearSelectedUser());
  }, [dispatch]);

  // ───────────────────────── Computed Values ─────────────────────────

  const getUserInfluencerPurchases = useCallback((userId) => {
    return influencerPurchases[userId] || [];
  }, [influencerPurchases]);

  const isLoading = usersLoading || selectedUserLoading || actionLoading;
  const hasError = usersError || selectedUserError || actionError;

  // ───────────────────────── Return Hook Interface ─────────────────────────

  return {
    // State
    users,
    usersLoading,
    usersError,
    usersPage,
    usersRowsPerPage,
    usersTotalElements,
    usersTotalPages,
    
    selectedUser,
    selectedUserLoading,
    selectedUserError,
    
    actionLoading,
    actionError,
    
    videoPurchases,
    videoPurchasesLoading,
    videoPurchasesError,
    videoPurchasesPage,
    videoPurchasesRowsPerPage,
    videoPurchasesTotalElements,
    videoPurchasesTotalPages,
    
    influencerPurchases,
    influencerPurchasesLoading,
    influencerPurchasesError,
    
    footageHours,
    footageHoursLoading,
    footageHoursError,
    footageHoursPage,
    footageHoursRowsPerPage,
    footageHoursTotalElements,
    footageHoursTotalPages,
    
    exportLoading,
    exportError,
    
    // Computed values
    isLoading,
    hasError,
    
    // Actions
    loadUsers,
    loadUserById,
    handleEnableUser,
    handleDisableUser,
    handleAddUserRole,
    handleRemoveUserRole,
    handleDeleteUser,
    
    loadUserVideoPurchases,
    loadUserLast10PurchasesAfterInfluencerCodeUsage,
    handleExportUsersPurchasesToExcel,
    loadUserFootagePurchaseHours,
    
    handleUsersPageChange,
    handleUsersRowsPerPageChange,
    handleVideoPurchasesPageChange,
    handleVideoPurchasesRowsPerPageChange,
    handleFootageHoursPageChange,
    handleFootageHoursRowsPerPageChange,
    
    clearAllErrors,
    clearUserDetails,
    
    // Utility functions
    getUserInfluencerPurchases
  };
};
